import { useCallback, useEffect, useState } from 'react';
import { ALL, Lite, QueryFilterOperators } from 'types/api';
import { OrgPartSchema, PublicPartSchema } from 'types/clientV2/parts';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  BASE_DEFAULT_VALUES,
  OrgPartFormConstants,
} from 'ui-component/CreateNewOrgPartDialog/constants';
import _ from 'lodash';
import { handleErr } from 'utils/functions';
import {
  useLazyGetOrgPartsQuery,
  useLazySearchPublicPartsQuery,
} from 'store/slices/clientV2/parts';

export default function useFindParts() {
  const [foundCustomPart, setFoundCustomPart] =
    useState<Lite<OrgPartSchema> | null>(null);
  const [foundPublicPart, setFoundPublicPart] =
    useState<PublicPartSchema | null>(null);

  const { control, setValue, formState } = useFormContext();

  const [getOrgPart] = useServerSideLazyQuery<
    OrgPartSchema,
    Lite<OrgPartSchema>
  >(useLazyGetOrgPartsQuery);

  const [
    searchPublicParts,
    {
      isSuccess: isSuccessSearchPublicPart,
      isFetching: isFetchingSearchPublicPart,
    },
  ] = useLazySearchPublicPartsQuery();

  const watchCustomPartNumber = useWatch({
    control,
    name: OrgPartFormConstants.orgPartNumber.id,
  });

  const watchMpn = useWatch({
    control,
    name: OrgPartFormConstants.mpn.id,
  });

  const watchMfg = useWatch({
    control,
    name: OrgPartFormConstants.mfg.id,
  });

  const watchScrapedFields = useWatch({
    control,
    name: OrgPartFormConstants.scrapedFields.id,
  });

  const debouncedCustomPartSearch = useCallback(
    _.debounce(async (partNumber) => {
      const part = await getOrgPart({
        schema: [ALL],
        filters: [
          {
            field: 'customId',
            operator: QueryFilterOperators['='],
            value: partNumber,
          },
        ],
      }).unwrap();
      const foundPart = part.data?.[0] as Lite<OrgPartSchema>;
      setFoundCustomPart(foundPart ?? null);
    }, 500),
    [getOrgPart]
  );

  const handleNoFoundMatchingPart = useCallback(() => {
    // Clear auto-populated fields when public part is no longer valid
    // Only clear fields that are not dirty (user-edited) and not scraped
    const fieldsToClear = [
      OrgPartFormConstants.linkMatchedPart.id,
      OrgPartFormConstants.publicPart.id,
      OrgPartFormConstants.msl.id,
      OrgPartFormConstants.packageType.id,
      OrgPartFormConstants.description.id,
      OrgPartFormConstants.classification.id,
      OrgPartFormConstants.mountType.id,
    ];

    fieldsToClear.forEach((field) => {
      if (
        !formState.dirtyFields[field] &&
        !_.includes(watchScrapedFields, field)
      ) {
        setValue(
          field,
          BASE_DEFAULT_VALUES[field as keyof typeof BASE_DEFAULT_VALUES]
        );
      }
    });
  }, [setValue, formState.dirtyFields, watchScrapedFields]);

  const handleFoundMatchingPart = useCallback(
    (foundPart: PublicPartSchema) => {
      const formUpdates = {
        [OrgPartFormConstants.linkMatchedPart.id]: true,
        [OrgPartFormConstants.publicPart.id]: foundPart.id,
        [OrgPartFormConstants.msl.id]: foundPart.msl,
        [OrgPartFormConstants.packageType.id]: foundPart.package,
        [OrgPartFormConstants.description.id]: foundPart.description,
        [OrgPartFormConstants.classification.id]: foundPart.primaryClass,
        [OrgPartFormConstants.mountType.id]: foundPart.terminationType,
      };

      _.chain(formUpdates)
        .omit(watchScrapedFields)
        .entries()
        .forEach(([field, value]) => {
          setValue(field, value);
        })
        .value();
    },
    [setValue, watchScrapedFields]
  );

  const searchPublicPart = useCallback(
    async (mpn: string, mfg?: string) => {
      try {
        const part = await searchPublicParts({
          mpn,
          mfr: mfg,
        }).unwrap();
        const foundPart = part.data?.[0] as PublicPartSchema;
        if (foundPart) {
          setFoundPublicPart(foundPart);
        } else {
          setFoundPublicPart(null);
        }
      } catch (error) {
        handleErr(error, () => {});
        setFoundPublicPart(null);
      }
    },
    [searchPublicParts]
  );

  useEffect(() => {
    if (watchCustomPartNumber) {
      debouncedCustomPartSearch(watchCustomPartNumber);
    }
  }, [watchCustomPartNumber, debouncedCustomPartSearch]);

  useEffect(
    () => () => {
      debouncedCustomPartSearch.cancel();
    },
    [debouncedCustomPartSearch]
  );

  useEffect(() => {
    // Validate that the found public part matches the current mpn and, if provided, mfg
    // To handle case where the mpn and mfg fields have been updated since the public part was found
    const isPartValid =
      foundPublicPart &&
      foundPublicPart.mpn.toLowerCase() === watchMpn?.toLowerCase() &&
      (!watchMfg ||
        foundPublicPart.manufacturer.toLowerCase() === watchMfg.toLowerCase());

    if (!isPartValid) {
      handleNoFoundMatchingPart();
      return;
    }

    handleFoundMatchingPart(foundPublicPart);
  }, [
    foundPublicPart,
    watchMpn,
    watchMfg,
    handleNoFoundMatchingPart,
    handleFoundMatchingPart,
  ]);

  return {
    foundCustomPart,
    foundPublicPart,
    isSuccessSearchPublicPart,
    isFetchingSearchPublicPart,
    searchPublicPart,
  };
}
